import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import BackToTop from '../Back-to-top/BackToTop';
import { useCompany } from '../../Contexts/CompanyContext';
import {
  searchOrder,
  fetchOrderById,
  TrackedOrder,
  OrderStatus,
  ORDER_STATUS_STEPS,
  getStatusStepIndex,
  getStatusColor,
  getPaymentStatusColor,
  getVariantText,
} from '../../Services/ordertrackingservice';
import HeaderSpacer from '../Header/HeaderSpacer';

/* ─────────────────────────── STATUS BADGE ─────────────────────────── */
const StatusBadge: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <span
    style={{
      display: 'inline-block',
      backgroundColor: color,
      color: color === '#ffc107' ? '#000' : '#fff',
      padding: '4px 14px',
      borderRadius: '4px',
      fontWeight: 600,
      fontSize: '12px',
      textTransform: 'capitalize',
      letterSpacing: '0.5px',
    }}
  >
    {label.replace(/_/g, ' ')}
  </span>
);

/* ─────────────────────────── PROGRESS TRACKER ─────────────────────── */
const ProgressTracker: React.FC<{ status: OrderStatus }> = ({ status }) => {
  const isCancelled = status === 'cancelled' || status === 'returned';
  const currentStep = isCancelled ? -1 : getStatusStepIndex(status);

  return (
    <div className="ot-progress-wrapper">
      {isCancelled ? (
        <div className="ot-cancelled-notice">
          <i className="zmdi zmdi-close-circle" style={{ fontSize: 36, color: '#dc3545', marginBottom: 10 }}></i>
          <p className="stext-113 cl2" style={{ fontWeight: 600 }}>
            This order has been <span style={{ color: '#dc3545', textTransform: 'capitalize' }}>{status}</span>.
          </p>
        </div>
      ) : (
        <div className="ot-steps">
          {ORDER_STATUS_STEPS.map((step, index) => {
            const isCompleted = index <= currentStep;
            const isActive = index === currentStep;
            return (
              <React.Fragment key={step.key}>
                <div className={`ot-step ${isCompleted ? 'ot-step--done' : ''} ${isActive ? 'ot-step--active' : ''}`}>
                  <div className="ot-step__circle">
                    <i className={`zmdi ${step.icon}`}></i>
                  </div>
                  <div className="ot-step__label">{step.label}</div>
                  {isActive && (
                    <div className="ot-step__desc">{step.description}</div>
                  )}
                </div>
                {index < ORDER_STATUS_STEPS.length - 1 && (
                  <div className={`ot-connector ${index < currentStep ? 'ot-connector--done' : ''}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────── PRODUCT IMAGE ────────────────────────── */
const PLACEHOLDER = '/asset/images/product-placeholder.png';
const ItemImage: React.FC<{ src?: string; alt: string }> = ({ src, alt }) => {
  const [error, setError] = useState(false);
  useEffect(() => { setError(false); }, [src]);
  return (
    <img
      src={!src || error ? PLACEHOLDER : src}
      alt={alt}
      onError={() => setError(true)}
      style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4, display: 'block', flexShrink: 0 }}
    />
  );
};

/* ─────────────────────────── MAIN PAGE ────────────────────────────── */
const OrderTracking: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { formatPrice } = useCompany();

  // Form state
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [searching, setSearching] = useState(false);

  // Result state
  const [order, setOrder] = useState<TrackedOrder | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Pre-fill from query param (e.g. coming from confirmation page)
  useEffect(() => {
    const orderId = searchParams.get('orderId');
    if (orderId) {
      // If we have the firestore doc ID, load directly (no email needed)
      (async () => {
        setSearching(true);
        try {
          const result = await fetchOrderById(orderId);
          if (result) {
            setOrder(result);
            setOrderNumber(result.orderNumber);
          } else {
            setNotFound(true);
          }
        } finally {
          setSearching(false);
        }
      })();
    }
  }, [searchParams]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setNotFound(false);
    setOrder(null);
    setSearching(true);

    try {
      const { order: found, error } = await searchOrder(orderNumber, email);
      if (error) {
        setFormError(error);
      } else if (found) {
        setOrder(found);
      } else {
        setNotFound(true);
      }
    } finally {
      setSearching(false);
    }
  };

  const handleReset = () => {
    setOrder(null);
    setNotFound(false);
    setFormError('');
    setOrderNumber('');
    setEmail('');
    navigate('/order-tracking', { replace: true });
  };

  return (
    <div>
      <Header />
      <HeaderSpacer />

      {/* ── Breadcrumb ── */}
      <div className="container">
        <div className="bread-crumb flex-w p-l-25 p-r-15 p-t-30 p-lr-0-lg">
          <a href="/" className="stext-109 cl8 hov-cl1 trans-04">
            Home
            <i className="fa fa-angle-right m-l-9 m-r-10" aria-hidden="true"></i>
          </a>
          <span className="stext-109 cl4">Track Order</span>
        </div>
      </div>

      <section className="bg0 p-t-75 p-b-120">
        <div className="container">

          {/* ── Page Header ── */}
          <div className="text-center p-b-50">
            <i
              className="zmdi zmdi-truck"
              style={{ fontSize: 64, color: '#717fe0', marginBottom: 16, display: 'block' }}
            ></i>
            <h2 className="mtext-111 cl2 p-b-10">Track Your Order</h2>
            <p className="stext-113 cl6">
              Enter your order number and email address to track the status of your order.
            </p>
          </div>

          {/* ── Search Form (shown when no order loaded) ── */}
          {!order && (
            <div className="ot-form-wrapper">
              <div className="bor10 p-lr-40 p-t-30 p-b-40 ot-form-card">
                <h4 className="mtext-109 cl2 p-b-25">Find Your Order</h4>

                <form onSubmit={handleSearch} noValidate>
                  {/* Order Number */}
                  <div className="p-b-20">
                    <label className="stext-110 cl2 p-b-10" style={{ display: 'block', marginBottom: 8 }}>
                      Order Number <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <div className="bor8 bg0">
                      <input
                        className="stext-111 cl8 plh3 size-111 p-lr-15"
                        type="text"
                        placeholder="e.g. ORD-1718000000000-ABC"
                        value={orderNumber}
                        onChange={(e) => {
                          setOrderNumber(e.target.value);
                          setFormError('');
                        }}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="p-b-20">
                    <label className="stext-110 cl2 p-b-10" style={{ display: 'block', marginBottom: 8 }}>
                      Email Address <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <div className="bor8 bg0">
                      <input
                        className="stext-111 cl8 plh3 size-111 p-lr-15"
                        type="email"
                        placeholder="email@example.com"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setFormError('');
                        }}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>

                  {/* Error */}
                  {formError && (
                    <p
                      className="stext-111 p-b-15"
                      style={{ color: '#dc3545', fontSize: 13 }}
                    >
                      <i className="zmdi zmdi-alert-circle m-r-5"></i>
                      {formError}
                    </p>
                  )}

                  {/* Not found */}
                  {notFound && !formError && (
                    <p
                      className="stext-111 p-b-15"
                      style={{ color: '#dc3545', fontSize: 13 }}
                    >
                      <i className="zmdi zmdi-alert-circle m-r-5"></i>
                      No order found. Please double-check your order number and email.
                    </p>
                  )}

                  <button
                    type="submit"
                    className="flex-c-m stext-101 cl0 size-116 bg3 bor14 hov-btn3 p-lr-15 trans-04 pointer"
                    disabled={searching}
                    style={{ width: '100%', opacity: searching ? 0.7 : 1 }}
                  >
                    {searching ? (
                      <>
                        <span
                          className="ot-spinner"
                          style={{ marginRight: 8 }}
                        ></span>
                        Searching...
                      </>
                    ) : (
                      <>
                        <i className="zmdi zmdi-search m-r-8"></i>
                        Track Order
                      </>
                    )}
                  </button>
                </form>

                {/* Helper */}
                <p className="stext-111 cl6 p-t-20" style={{ fontSize: 12, textAlign: 'center' }}>
                  Your order number is included in your confirmation email.
                </p>
              </div>
            </div>
          )}

          {/* ── Order Result ── */}
          {order && (
            <div className="ot-result">

              {/* Back button */}
              <div className="p-b-30 text-center">
                <button
                  onClick={handleReset}
                  className="flex-c-m stext-101 cl2 size-119 bg8 bor13 hov-btn3 p-lr-15 trans-04 pointer"
                  style={{ margin: '0 auto' }}
                >
                  <i className="zmdi zmdi-arrow-left m-r-8"></i>
                  Track Another Order
                </button>
              </div>

              {/* ── Progress Tracker ── */}
              <div className="bor10 p-lr-40 p-t-30 p-b-40 m-b-30 ot-section">
                <h4 className="mtext-109 cl2 p-b-25">Order Status</h4>
                <ProgressTracker status={order.status} />
              </div>

              {order.stockReservationFailed && (
                <div
                  className="p-lr-15 p-tb-15 m-b-30"
                  style={{ background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: 8 }}
                >
                  <p className="stext-111" style={{ color: '#8a5300', margin: 0 }}>
                    We're reviewing the stock for one or more items in this order. Our team will
                    contact you shortly to confirm availability — no action is needed from you
                    right now.
                  </p>
                </div>
              )}

              <div className="row">
                {/* ── Left column: order info + address ── */}
                <div className="col-lg-7 m-b-30">
                  <div className="bor10 p-lr-40 p-t-30 p-b-40 ot-section" style={{ height: '100%' }}>
                    <h4 className="mtext-109 cl2 p-b-25">Order Details</h4>

                    {[
                      { label: 'Order Number', value: order.orderNumber },
                      {
                        label: 'Order Date',
                        value: (() => {
                          const d = typeof (order.orderDate as any).toDate === 'function'
                            ? (order.orderDate as any).toDate()
                            : new Date(order.orderDate as any);
                          return d.toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          });
                        })(),
                      },
                      {
                        label: 'Order Status',
                        value: (
                          <StatusBadge
                            label={order.status}
                            color={getStatusColor(order.status)}
                          />
                        ),
                      },
                      {
                        label: 'Payment Status',
                        value: (
                          <StatusBadge
                            label={order.paymentStatus}
                            color={getPaymentStatusColor(order.paymentStatus)}
                          />
                        ),
                      },
                      {
                        label: 'Payment Method',
                        value: order.paymentMethod.replace(/_/g, ' '),
                      },
                      ...(order.trackingNumber
                        ? [{ label: 'Tracking Number', value: order.trackingNumber }]
                        : []),
                      ...(order.estimatedDelivery
                        ? [{ label: 'Estimated Delivery', value: order.estimatedDelivery }]
                        : []),
                    ].map(({ label, value }) => (
                      <div className="flex-w flex-t p-b-13" key={label}>
                        <div className="ot-label-col">
                          <span className="stext-110 cl2">{label}:</span>
                        </div>
                        <div className="ot-value-col">
                          {typeof value === 'string' ? (
                            <span className="stext-111 cl6" style={{ textTransform: label === 'Payment Method' ? 'capitalize' : 'none' }}>
                              {value}
                            </span>
                          ) : (
                            value
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Shipping address */}
                    <div className="p-t-20">
                      <h5 className="mtext-110 cl2 p-b-15" style={{ borderTop: '1px solid #e6e6e6', paddingTop: 20 }}>
                        Shipping Address
                      </h5>
                      {order.shippingAddress && (
                        <div className="ot-address-block">
                          <p className="stext-111 cl6">{order.shippingAddress.fullName}</p>
                          <p className="stext-111 cl6">{order.shippingAddress.addressLine1}</p>
                          {order.shippingAddress.addressLine2 && (
                            <p className="stext-111 cl6">{order.shippingAddress.addressLine2}</p>
                          )}
                          <p className="stext-111 cl6">
                            {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}
                          </p>
                          <p className="stext-111 cl6">{order.shippingAddress.country}</p>
                          <p className="stext-111 cl6">{order.shippingAddress.phoneNumber}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Right column: order summary ── */}
                <div className="col-lg-5 m-b-30">
                  <div className="bor10 p-lr-40 p-t-30 p-b-40 ot-section" style={{ height: '100%' }}>
                    <h4 className="mtext-109 cl2 p-b-25">Order Summary</h4>

                    {/* Items */}
                    <div className="p-b-20" style={{ borderBottom: '1px solid #e6e6e6' }}>
                      {order.items.map((item, index) => (
                        <div
                          key={index}
                          className="ot-item"
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 12,
                            marginBottom: 16,
                          }}
                        >
                          <ItemImage src={item.productImage} alt={item.productName} />
                          <div style={{ flex: 1 }}>
                            <p className="stext-111 cl2" style={{ fontWeight: 500, marginBottom: 2 }}>
                              {item.productName}
                            </p>
                            {item.variantSelections && item.variantSelections.length > 0 && (
                              <p className="stext-111 cl6" style={{ fontSize: 12, marginBottom: 2 }}>
                                {getVariantText(item.variantSelections)}
                              </p>
                            )}
                            <p className="stext-111 cl6" style={{ fontSize: 12 }}>
                              Qty: {item.quantity} × {formatPrice(item.unitPrice)}
                            </p>
                          </div>
                          <span className="stext-111 cl2" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {formatPrice(item.totalPrice)}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Totals */}
                    {[
                      { label: 'Subtotal', value: formatPrice(order.subtotal) },
                      { label: 'Tax', value: formatPrice(order.taxAmount) },
                      {
                        label: 'Shipping',
                        value: order.shippingCost === 0 ? 'Free' : formatPrice(order.shippingCost),
                      },
                      ...(order.discountAmount > 0
                        ? [{ label: 'Discount', value: `-${formatPrice(order.discountAmount)}` }]
                        : []),
                    ].map(({ label, value }) => (
                      <div className="flex-w flex-t p-t-13 p-b-5" key={label}>
                        <div className="size-208">
                          <span className="stext-110 cl2">{label}</span>
                        </div>
                        <div className="size-209">
                          <span className="stext-111 cl6">{value}</span>
                        </div>
                      </div>
                    ))}

                    {/* Grand total */}
                    <div
                      className="flex-w flex-t p-t-20"
                      style={{ borderTop: '2px solid #333', marginTop: 10 }}
                    >
                      <div className="size-208">
                        <span className="mtext-101 cl2">Total</span>
                      </div>
                      <div className="size-209">
                        <span className="mtext-110 cl2" style={{ fontWeight: 700 }}>
                          {formatPrice(order.totalAmount)}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="p-t-30 flex-w" style={{ gap: 10 }}>
                      <a
                        href="/"
                        className="flex-c-m stext-101 cl0 size-116 bg3 bor14 hov-btn3 p-lr-15 trans-04 pointer"
                        style={{ flex: 1 }}
                      >
                        Continue Shopping
                      </a>
                      <button
                        onClick={() => window.print()}
                        className="flex-c-m stext-101 cl2 size-116 bg8 bor13 hov-btn3 p-lr-15 trans-04 pointer"
                        style={{ flex: 1 }}
                      >
                        <i className="zmdi zmdi-print m-r-5"></i>
                        Print
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── What's next info ── */}
              {order.status !== 'delivered' &&
                order.status !== 'cancelled' &&
                order.status !== 'returned' && (
                  <div className="bor10 p-lr-40 p-t-30 p-b-40 ot-section text-center m-t-10">
                    <h5 className="mtext-110 cl2 p-b-25">What Happens Next?</h5>
                    <div className="row">
                      {[
                        {
                          icon: 'zmdi-email',
                          title: 'Email Updates',
                          desc: "We'll email you when your order status changes.",
                        },
                        {
                          icon: 'zmdi-settings',
                          title: 'Processing',
                          desc: "We're preparing your items for shipment.",
                        },
                        {
                          icon: 'zmdi-truck',
                          title: 'Shipping',
                          desc: "You'll receive a tracking number once your order ships.",
                        },
                      ].map(({ icon, title, desc }) => (
                        <div className="col-md-4 p-b-20" key={title}>
                          <i
                            className={`zmdi ${icon}`}
                            style={{ fontSize: 36, color: '#717fe0', marginBottom: 12, display: 'block' }}
                          ></i>
                          <p className="stext-113 cl2 p-b-5">
                            <strong>{title}</strong>
                          </p>
                          <p className="stext-113 cl6">{desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>
      </section>

      <Footer />
      <BackToTop />

      <style>{`
        /* ── Form card ── */
        .ot-form-wrapper {
          max-width: 540px;
          margin: 0 auto;
        }
        .ot-form-card {
          background: #fff;
        }

        /* ── Section card ── */
        .ot-section {
          background: #fff;
        }

        /* ── Detail row columns ── */
        .ot-label-col {
          width: 42%;
          padding-right: 10px;
        }
        .ot-value-col {
          width: 58%;
        }

        /* ── Address block ── */
        .ot-address-block p {
          margin-bottom: 4px;
          line-height: 1.6;
        }

        /* ── Progress tracker ── */
        .ot-progress-wrapper {
          padding: 10px 0 0;
        }
        .ot-steps {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          position: relative;
          flex-wrap: wrap;
          gap: 0;
        }
        .ot-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex: 1;
          min-width: 80px;
          position: relative;
          z-index: 1;
        }
        .ot-step__circle {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: 2px solid #e6e6e6;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          color: #ccc;
          transition: all 0.3s ease;
          margin-bottom: 10px;
        }
        .ot-step--done .ot-step__circle {
          border-color: #717fe0;
          background: #717fe0;
          color: #fff;
        }
        .ot-step--active .ot-step__circle {
          border-color: #717fe0;
          background: #fff;
          color: #717fe0;
          box-shadow: 0 0 0 4px rgba(113, 127, 224, 0.15);
        }
        .ot-step__label {
          font-size: 12px;
          font-weight: 600;
          color: #aaa;
          text-align: center;
          transition: color 0.3s;
          white-space: nowrap;
        }
        .ot-step--done .ot-step__label,
        .ot-step--active .ot-step__label {
          color: #333;
        }
        .ot-step__desc {
          font-size: 11px;
          color: #717fe0;
          text-align: center;
          margin-top: 4px;
          max-width: 100px;
        }
        .ot-connector {
          flex: 1;
          height: 2px;
          background: #e6e6e6;
          margin-top: 24px;
          transition: background 0.3s;
          min-width: 16px;
          max-width: 80px;
        }
        .ot-connector--done {
          background: #717fe0;
        }

        /* ── Cancelled notice ── */
        .ot-cancelled-notice {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 20px 0;
        }

        /* ── Spinner ── */
        .ot-spinner {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: ot-spin 0.7s linear infinite;
          vertical-align: middle;
        }
        @keyframes ot-spin {
          to { transform: rotate(360deg); }
        }

        /* ── Input override ── */
        .bor8.bg0 input {
          width: 100%;
          padding: 10px 15px;
          border: none;
          background: transparent;
          font-size: 13px;
          color: #555;
          font-family: Poppins-Regular, sans-serif;
          outline: none;
        }

        /* ── Responsive ── */
        @media (max-width: 576px) {
          .ot-label-col,
          .ot-value-col {
            width: 100%;
          }
          .ot-steps {
            gap: 4px;
          }
          .ot-step__label {
            font-size: 10px;
          }
          .ot-connector {
            min-width: 8px;
          }
          .ot-form-wrapper {
            padding: 0 15px;
          }
        }

        /* ── Print ── */
        @media print {
          header, footer, .bread-crumb, .ot-form-wrapper,
          button, a.bg3, a.bg8 { display: none !important; }
          .ot-result { margin: 0; }
        }
      `}</style>
    </div>
  );
};

export default OrderTracking;